import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { initializeDistanceSlider, initializeAgeRangeSlider, destroySlider } from '../../utils/sliders';
import { Offcanvas } from 'bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setAgeSearch, setGenderSearch } from '../../store/slices/authSlice';
import { setDistance, setRefreshProfiles } from '../../store/slices/preferencesSlice';

import { Select } from 'antd';
import { useTranslation } from 'react-i18next';

const SettingsOffcanvasContent = ({ children }) => {
  const { t } = useTranslation();
  const slidersRef = useRef({ distance: null, age: null });
  const offcanvasRef = useRef(null);
  const ageSliderRef = useRef(null);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [tempSettings, setTempSettings] = useState({
    distance: null,
    ageSearch: null
  });
  const authState = useSelector(state => state.auth);
  const preferences = useSelector(state => state.preferences);
  const currentDistance = preferences?.distance || 100;

  // Fonction d'initialisation des sliders qui utilise les valeurs actuelles
  const initializeSliders = () => {
    console.log("Checking sliders initialization with:", { distance: currentDistance, ageSearch: authState.ageSearch });
    
    // Initialiser le slider de distance seulement s'il n'existe pas
    if (!slidersRef.current.distance?.noUiSlider) {
      console.log("Initializing distance slider");
      slidersRef.current.distance = initializeDistanceSlider(currentDistance);
      slidersRef.current.distance.noUiSlider.on('change', function(values) {
        const newDistance = parseInt(values[0]);
        console.log("Distance changed to:", newDistance);
        setTempSettings(prev => ({
          ...prev,
          distance: newDistance
        }));
      });
    }

    // Initialiser le slider d'âge seulement s'il n'existe pas
    if (!slidersRef.current.age?.noUiSlider) {
      console.log("Initializing age slider");
      slidersRef.current.age = initializeAgeRangeSlider(authState.ageSearch);
      ageSliderRef.current = slidersRef.current.age;
      ageSliderRef.current.noUiSlider.on('change', function(values) {
        const newAgeSearch = [parseInt(values[0]), parseInt(values[1])];
        console.log("Age range changed to:", newAgeSearch);
        setTempSettings(prev => ({
          ...prev,
          ageSearch: newAgeSearch
        }));
      });
    }
  };

  const handleApplySettings = () => {
    setLoading(true);
    try {
      console.log("Applying settings:", tempSettings);
      if (tempSettings.distance !== null) {
        dispatch(setDistance(tempSettings.distance));
      }
      if (tempSettings.ageSearch) {
        console.log("Setting age search to:", tempSettings.ageSearch);
        dispatch(setAgeSearch(tempSettings.ageSearch));
      }
      
      const offcanvas = Offcanvas.getInstance(offcanvasRef.current);
      if (offcanvas) {
        offcanvas.hide();
      }
      dispatch(setRefreshProfiles(true));
    } finally {
      setLoading(false);
    }
  };

  // Gestion de l'initialisation et du nettoyage de l'offcanvas
  useEffect(() => {
    // Copie des refs dans des variables locales pour que le cleanup utilise toujours la même référence
    const offcanvasEl = offcanvasRef.current;
    const sliders = slidersRef.current;

    if (offcanvasEl) {
      new Offcanvas(offcanvasEl);

      const handleShown = () => {
        console.log("Canvas shown");
        initializeSliders();
      };

      const handleHidden = () => {
        console.log("Canvas hidden");
        // Réinitialiser les paramètres temporaires sans détruire les sliders
        setTempSettings({
          distance: null,
          ageSearch: null
        });
      };

      offcanvasEl.addEventListener('shown.bs.offcanvas', handleShown);
      offcanvasEl.addEventListener('hidden.bs.offcanvas', handleHidden);

      return () => {
        offcanvasEl.removeEventListener('shown.bs.offcanvas', handleShown);
        offcanvasEl.removeEventListener('hidden.bs.offcanvas', handleHidden);
        // Nettoyer les sliders seulement quand le composant est démonté
        if (sliders.distance?.noUiSlider) {
          destroySlider(sliders.distance);
        }
        if (sliders.age?.noUiSlider) {
          destroySlider(sliders.age);
        }
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mettre à jour les sliders quand les valeurs Redux changent
  useEffect(() => {
    console.log("Updating sliders with:", { distance: currentDistance, ageSearch: authState.ageSearch });
    if (slidersRef.current.distance?.noUiSlider) {
      console.log("Updating distance slider to:", currentDistance);
      slidersRef.current.distance.noUiSlider.set(currentDistance);
    }
    if (slidersRef.current.age?.noUiSlider) {
      console.log("Updating age slider to:", authState.ageSearch);
      slidersRef.current.age.noUiSlider.set(authState.ageSearch);
    }
  }, [currentDistance, authState.ageSearch]);

  return (
    <>
      <div className="offcanvas offcanvas-bottom container p-0" tabIndex="-1" id="settingCanvas" ref={offcanvasRef}>
        <button type="button" className="btn-close drage-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
        <div className="offcanvas-header share-style m-0">
          <h5 className="title mb-0">{t('settings.discovery.title')}</h5>
          <button type="button" className="btn-close" data-bs-dismiss="offcanvas" aria-label="Close">
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>
        <div className="offcanvas-body">
          <div className="setting-content">
            <div className="mb-3">
              <label className="form-label d-flex justify-content-between">
                <span>{t('settings.discovery.showMe')}</span>
              </label>
              <Select
                className="ant-select-show-me"
                value={authState.genderSearch}
                onChange={(value) => dispatch(setGenderSearch(value))}
                options={[
                  { value: 'F', label: t('settings.discovery.gender.women') },
                  { value: 'M', label: t('settings.discovery.gender.men') },
                  { value: 'ALL', label: t('settings.discovery.gender.everyone') }
                ]}
              />
            </div>
            <div className="dz-slider mb-3">
              <div className="slider-head">
                <h6 className="title mb-0">{t('settings.discovery.distance.title')}</h6>
                <div className="title font-w600 font-16">
                  <span className="example-val title slider-margin-value-min"></span>
                  <span className="example-val title slider-margin-value-max"></span>
                </div>
              </div>
              <div className="slider-body">
                <div className="range-slider style-1 w-100">
                  <div id="slider-tooltips3"></div>
                </div>
              </div>
            </div>
            <div className="dz-slider">
              <div className="slider-head">
                <h6 className="title mb-0">{t('settings.discovery.age.title')}</h6>
                <div className="title font-w600 font-16">
                  <span className="example-val title slider-margin-value-min">-</span>
                  <span className="example-val title slider-margin-value-max"></span>
                </div>
              </div>
              <div className="slider-body">
                <div className="range-slider style-1 w-100">
                  <div id="slider-tooltips4"></div>
                </div>
              </div>
            </div>
            <div className="mt-4 px-3">
              <button 
                type="button" 
                className="btn btn-primary btn-block w-100"
                onClick={handleApplySettings}
                disabled={loading}
              >
                {loading ? (
                  <span>
                    <i className="fas fa-spinner fa-spin me-2"></i>
                    {t('settings.discovery.updating')}
                  </span>
                ) : (
                  t('settings.discovery.applyChanges')
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const SettingsOffcanvas = () => {
  return ReactDOM.createPortal(
    <SettingsOffcanvasContent />,
    document.body
  );
};

export default SettingsOffcanvas;
